import type { PlotSeries } from "@/shared/state/visualization";
import type { XAxisAlignment } from "@/shared/state/visualization/schema/v1";
import { nanoSecToSecLossy } from "@/shared/time";

import { TimeDisplayMode } from "../../WorkspaceCtx/TimeDisplayMode";

import type { Bounds, LogTimeBounds } from "./PlotDataStreamer";

/**
 * How one series plots in aligned mode.
 *
 * Resolved once per panel by `resolveSeriesAlignment` and read by every
 * aligned consumer: the worker's per-series x-axis bases and chart domain,
 * the playhead, click-to-seek, and the legend's and tooltip's fallback
 * flags.
 */
export interface AlignedSeries {
  /** The absolute timestamp this series plots as x = 0. */
  basis: bigint;
  /**
   * Identity of the recording the series belongs to: its file's workspace
   * source when one exists, else the series' own topic. Series that share a
   * `sourceId` and a `basis` plot at the same x, so their playhead lines
   * collapse into one.
   */
  sourceId: string;
  /**
   * The recording's absolute span: a workspace source's span, already
   * narrowed by the session's clip range, or the series' own persisted topic
   * bounds when its file matches no source. The playhead draws only while
   * the current time is inside it, and click-to-seek targets are confined to
   * it.
   */
  sourceStartTime: bigint;
  sourceEndTime: bigint;
  /**
   * True when the anchor the panel's alignment asks for could not be found
   * for this series and `basis` fell back to `sourceStartTime`; the legend
   * and tooltip flag these.
   */
  isFallback: boolean;
}

/**
 * Where an aligned panel plots each of its series, keyed by series id.
 *
 * A series absent from `seriesById` carries no usable time information: its
 * file belongs to no workspace source and its own topic has no parseable
 * persisted time bounds. Consumers agree on how to handle it: the worker
 * plots it against the panel-wide basis, it contributes nothing to the
 * aligned chart domain, it gets no playhead line and no seek target, and the
 * tooltip flags its row.
 */
export interface SeriesAlignment {
  seriesById: Record<string, AlignedSeries>;
}

/**
 * Whether this alignment puts x = 0 at a named event rather than at a session's
 * start or its first data point.
 *
 * The event anchor is the only one that reads the workspace's events, so the
 * hook that resolves an alignment gates on the events having loaded, and the
 * resolver builds its event lookup, from this one answer. Asking the question
 * separately in each place lets the two drift, which would leave the resolver
 * reading an event list the hook never waited for.
 */
export function isEventAnchored(
  alignment: XAxisAlignment | undefined,
): alignment is Extract<XAxisAlignment, { unit: "session" }> & {
  anchor: { eventName: string };
} {
  return alignment?.unit === "session" && typeof alignment.anchor === "object";
}

/**
 * Parse a persisted nanosecond-timestamp string. Persisted workspace state
 * is validated only as a string, so a malformed value must degrade to
 * `undefined` rather than throw out of a render (`BigInt("abc")` throws,
 * and `BigInt("")` is `0n`, which would silently anchor a series at 1970).
 */
export function parseNanosecondsString(
  value: string | null | undefined,
): bigint | undefined {
  if (value === undefined || value === null || !/^\d+$/.test(value)) {
    return undefined;
  }
  return BigInt(value);
}

/**
 * The absolute window of data a series shows: its persisted topic time
 * bounds narrowed to its recording's span, or that whole span when the
 * persisted bounds are absent or malformed. Undefined when the topic's
 * bounds fall entirely outside that span (a session clip range can exclude
 * one topic of a multi-topic file).
 */
function seriesDataWindow(
  series: PlotSeries,
  aligned: AlignedSeries,
): { start: bigint; end: bigint } | undefined {
  const topicStart = parseNanosecondsString(series.data.topic.startTime);
  const topicEnd = parseNanosecondsString(series.data.topic.endTime);
  if (topicStart === undefined || topicEnd === undefined) {
    return { start: aligned.sourceStartTime, end: aligned.sourceEndTime };
  }
  const start =
    topicStart > aligned.sourceStartTime ? topicStart : aligned.sourceStartTime;
  const end =
    topicEnd < aligned.sourceEndTime ? topicEnd : aligned.sourceEndTime;
  if (start > end) {
    return undefined;
  }
  return { start, end };
}

/**
 * The chart's time bounds and x-axis domain when the panel is aligned.
 *
 * In aligned mode every series plots x = seconds(logTime − its own basis),
 * so the chart's domain is the union of the series' elapsed windows:
 * [minElapsed, maxElapsed]. Each series' window comes from
 * {@link seriesDataWindow}, so a session clip range narrows the domain too.
 * minElapsed is 0 when every series is anchored at its recording's start,
 * and negative whenever a series' data begins before its own basis, as under
 * an event anchor that fires mid-recording.
 *
 * The absolute `time` window is a reference space the renderer interpolates
 * against: chart x ↔ reference time via `time.min + ns(x − domain.min)`,
 * with `time.min` chosen as `min(basis) + ns(domain.min)` so the window's
 * width matches the domain's.
 *
 * `basis` is that `min(basis)` itself, in exact nanoseconds: the absolute
 * timestamp the chart plots at x = 0. The data streamer shifts each series'
 * high-LOD read window into the series' own time by `its basis − basis`.
 *
 * Returns `undefined` when no series contributes a window, either because
 * none has an alignment entry or because every aligned series is clipped out
 * entirely; the caller keeps the panel's absolute bounds.
 */
export function alignedReferenceBounds(
  series: PlotSeries[],
  alignment: SeriesAlignment,
): { time: LogTimeBounds; domain: Bounds; basis: bigint } | undefined {
  let minElapsed: bigint | undefined;
  let maxElapsed: bigint | undefined;
  let minBasis: bigint | undefined;
  for (const plotSeries of series) {
    const aligned = alignment.seriesById[plotSeries.id];
    if (aligned === undefined) {
      continue;
    }
    const dataWindow = seriesDataWindow(plotSeries, aligned);
    if (dataWindow === undefined) {
      continue;
    }
    const elapsedStart = dataWindow.start - aligned.basis;
    const elapsedEnd = dataWindow.end - aligned.basis;
    if (minElapsed === undefined || elapsedStart < minElapsed) {
      minElapsed = elapsedStart;
    }
    if (maxElapsed === undefined || elapsedEnd > maxElapsed) {
      maxElapsed = elapsedEnd;
    }
    if (minBasis === undefined || aligned.basis < minBasis) {
      minBasis = aligned.basis;
    }
  }
  if (
    minElapsed === undefined ||
    maxElapsed === undefined ||
    minBasis === undefined
  ) {
    return undefined;
  }

  if (minElapsed === maxElapsed) {
    // A 0-width chart domain breaks render, pan, and zoom; widen it by a
    // nanosecond on each side, as PlotDataStreamer.setTimeBounds does.
    minElapsed -= 1n;
    maxElapsed += 1n;
  }

  return {
    time: {
      min: minBasis + minElapsed,
      max: minBasis + maxElapsed,
    },
    domain: {
      min: nanoSecToSecLossy(minElapsed),
      max: nanoSecToSecLossy(maxElapsed),
    },
    basis: minBasis,
  };
}

/**
 * The tick-label mode a panel renders with. An absolute mode (Log, UTC,
 * Local) names a moment no aligned series shares, since each plots elapsed
 * time from its own basis, so aligned panels coerce those to Elapsed; both
 * elapsed modes pass through. The switch has no default, so adding a
 * TimeDisplayMode member fails compilation here until it is handled.
 */
export function effectiveTimeDisplayMode(
  mode: TimeDisplayMode,
  isAligned: boolean,
): TimeDisplayMode {
  if (!isAligned) {
    return mode;
  }
  switch (mode) {
    case TimeDisplayMode.Log:
    case TimeDisplayMode.UTC:
    case TimeDisplayMode.Local:
      return TimeDisplayMode.Elapsed;
    case TimeDisplayMode.Elapsed:
    case TimeDisplayMode.ElapsedTime:
      return mode;
  }
}
